import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";

import { ThemeProvider } from "@mui/material/styles";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useWebsocket from "react-use-websocket";
import { v4 as uuidv4 } from "uuid";

import { useTenant } from "multitenancy/config";
import theme from "themes";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";
import { useMediaPermissions } from "hooks/useMediaPermissions";

import {
  setAudienceFilter,
  clearAudienceFilter as clearAudienceFilterAction,
} from "store/modules/audienceFilter/actions";
import { hideLoading, showLoading } from "store/modules/loading/actions";
import { useAppSelector } from "store/rootReducer";

import { AdminDashboard } from "features/AdminDashboard";
import CreateAttachmentTemplateAudiencia from "features/CreateAttachmentTemplateAudiencia";
import CreateAttachmentTemplateInquerito from "features/CreateAttachmentTemplateInquerito";
import { DownloadsByHearingFile } from "features/DownloadsByHearingFile";
import { LeftList } from "features/hearingRecords/LeftList";
import { ListRecorded } from "features/hearingRecords/ListRecorded";
import { LoadSpinner } from "features/LoadSpinner";
import { HearingFilter } from "features/Modal/HearingFilter";
import { IOptionsFilterStatus } from "features/Modal/HearingFilter/interface";
import VinculeDataToAnotherHearingModal from "features/ModalVinculeDataToAnotherHearing";
import { RightAudienceComponent } from "features/RightAudienceComponent";
import { RightTemplate } from "features/RightTemplate";
import ValidationModal from "features/ValidationModal";
import { useUploadingFile } from "features/ValidationModal/context";

import { Drawer } from "components/Drawer";
import { IHearingAttendant } from "components/ItemDetailLine/interface";
import ModalDefault from "components/ModalDefault";
import { ProcedureAttendant } from "components/ProcedureAttendant";

import api, { baseBlob } from "services/api";
import {
  AttendandoViculeService,
  AttendantService,
  procedureCustomerAttendantService,
} from "services/attendant";
import { IFilterParameters, audiencesServices } from "services/audiences";
import { HearingService } from "services/hearing";
import {
  getRequiredCustomFieldErrors,
  HearingCustomFieldService,
  IHearingCustomField,
} from "services/hearingCustomField";
import {
  hearingFileService,
  hearingFileServiceV2,
  IHearingFileBody,
  IHearingFileDownloads,
} from "services/hearingFile";
import { MeetingMConfService } from "services/meetingMConf";
import { ProcedureCustomerSourceService } from "services/procedureCustomerSource";
import { SystemConfigurationService } from "services/systemConfiguration";
import { getActualTime } from "services/utils/dateTime";

import { LogOperation } from "utils/entities/logOperation";
import { FORMS_HEARING } from "utils/entities/permission";
import { useQuery } from "utils/get";
import { stringFormat } from "utils/stringFormat";
import { verifyEmailFormat } from "utils/verifyEmailFormat";

import {
  CreateRecordEdit,
  ShowModalDeleteAlert,
  checkInputsFilled,
  getFirstRecord,
  handleChangeHearingsFromWebSocket,
  handleItemClicked,
  handleOpenDrawerDownloads,
  updateSchaduledList,
  updateStatusHearing,
  FilterExtras,
  onMigrateDataToAnotherHearing,
} from "./constants";
import CreateAudience from "./CreateAudience";
import CreateProcedure from "./CreateProcedure";
import {
  getAudienceConfidentialityLevel,
  isValidProcedureNumber,
  mapUserToServerLinkOption,
  normalizeConfidentialityLevel,
  normalizeServerLinkCpf,
  normalizeServerLinkEmail,
  normalizeServerLinkSearchTerm,
} from "./helpers";
import { ConfidentialityLevel, IAudience, IParticipant } from "./interface";
import ServerLinkDrawerContent, {
  IServerLinkOption,
} from "./ServerLinkDrawerContent";
import { Container } from "./styles";
import { CreateEditAttendantInAudience } from "../Attendant/CreateEditInAudience";
import { ServiceIntegrationNotification } from "../ServiceIntegrationNotification";

export const Audiences: React.FC = () => {
  const { t } = useTranslation();
  const { productId } = useTenant();
  const customization = useAppSelector((state) => state.customization);
  const permissions = useMediaPermissions();
  const isInqueritoProduct = productId === "inquerito";
  const isAudienceProduct = productId === "audiencias";
  const isCartorioProduct = productId === "cartorio";
  const usesBooleanConfidentiality = isCartorioProduct || isInqueritoProduct;
  //==================================================================================================================== States

  const [audience, setAudience] = useState<any>(undefined);
  const [audienceType, setAudienceType] = useState<any>("");
  const { userCustomerProfile, isAdmin } = useAppSelector(
    (store) => store.profile,
  );
  const userConfidentialityLevel = normalizeConfidentialityLevel(
    userCustomerProfile?.confidentialityLevel,
  );
  const defaultConfidentialityLevel = usesBooleanConfidentiality
    ? 0
    : userConfidentialityLevel;
  const { uploadingFile, setUploadingFile, loadingUpload } = useUploadingFile();

  const { editItem } = useAppSelector((store) => store.attachment);
  const { accessToken } = useAppSelector((state) => state.auth);

  const {
    managerHearingClassified,
    notify,
    hearingCorrection,
    hearingAttendantUnlink,
    viewOnlyYourRecords,
  } = usePermissions(FORMS_HEARING.HEARING);

  const { export: exportHearinDownloadsFiles } = usePermissions(
    FORMS_HEARING.HEARING_FILE,
  );

  const [nameAttendant, setNameAttendant] = useState<string>("");
  const [documentAttendant, setDocumentAttendant] = useState<string>("");
  const [emailAttendant, setEmailAttendant] = useState<string>("");
  const [active, setActive] = useState(true);

  const [record, setRecord] = useState<any>(null);
  const [listRecord, setListRecord] = useState<any | any[]>(null);
  const { addToast } = useToast();

  const [isEdit, setIsEdit] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalValidation, setOpenModalValidation] = useState(false);
  const [
    openModalVinculeDataToAnotherHearing,
    setOpenModalVinculeDataToAnotherHearing,
  ] = useState(false);
  const [openServerLinkDrawer, setOpenServerLinkDrawer] = useState(false);
  const [serverLinkInput, setServerLinkInput] = useState("");
  const [serverLinkOptions, setServerLinkOptions] = useState<
    IServerLinkOption[]
  >([]);
  const [linkedServers, setLinkedServers] = useState<IServerLinkOption[]>([]);
  const [serverLinksToCreate, setServerLinksToCreate] = useState<
    IServerLinkOption[]
  >([]);
  const [loadingServerLinkOptions, setLoadingServerLinkOptions] =
    useState(false);
  const [serverLinkFieldError, setServerLinkFieldError] = useState(false);
  const serverLinkSearchRequestRef = useRef(0);

  const [
    showModalConfirmationVinculeDataToAnotherHearing,
    setShowModalConfirmationVinculeDataToAnotherHearing,
  ] = useState(false);

  const [hearingToMigrate, setHearingToMigrate] = useState<any>(null);
  const [swapModeToMigrate, setSwapModeToMigrate] = useState<0 | 1 | 2>(0);
  const [openModalNotify, setOpenModalNotify] = useState(false);
  const [date, setDate] = useState<string | null>(null);
  const [hours, setHours] = useState<number | null>(null);
  const [confidentialityLevel, setConfidentialityLevel] =
    useState<ConfidentialityLevel>(defaultConfidentialityLevel);
  const [isClassified, setIsClassifiedState] = useState(false);

  const isConfidentialHearing = isClassified;
  const setIsClassified = (value: boolean) => {
    setIsClassifiedState(usesBooleanConfidentiality && value);
  };
  const setAudienceConfidentialityLevel = (value: number) => {
    setConfidentialityLevel(normalizeConfidentialityLevel(value));
  };
  const canChangeConfidentialityLevel = [4, 5].includes(
    userConfidentialityLevel,
  );
  const selectedAudienceConfidentialityLevel = useMemo(
    () =>
      normalizeConfidentialityLevel(
        getAudienceConfidentialityLevel(audience, usesBooleanConfidentiality),
      ),
    [
      audience?.ConfidentialityLevel,
      audience?.IsClassified,
      audience?.confidentialityLevel,
      audience?.isClassified,
      usesBooleanConfidentiality,
    ],
  );
  const selectedAudienceIsClassified = useMemo(
    () =>
      usesBooleanConfidentiality &&
      normalizeConfidentialityLevel(
        audience?.isClassified ?? audience?.IsClassified,
      ) > 0,
    [
      audience?.IsClassified,
      audience?.isClassified,
      usesBooleanConfidentiality,
    ],
  );
  const [loading, setLoading] = useState(false);
  const [editTypeSelected, setEditTypeSelected] = useState<any>("");
  const [nameAudience, setNameAudience] = useState<string>("");
  const [audienceDescription, setAudienceDescription] = useState<string>("");
  const [audiences, setAudiences] = useState<IAudience[]>([]);
  const [totalAudiences, setTotalAudiences] = useState(0);
  const [currentAudiencePage, setCurrentAudiencePage] = useState(0);
  const audiencePageSize = 200;
  const audienceRequestIdRef = useRef(0);
  const dispatch = useDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { customerCertificate } = useAppSelector(
    (s: any) => s.customerCertificate,
  );

  const disableIfNotCreate = user ? audience?.userId !== user.id : true;

  const [objectType, setObjectType] = useState<any | null>(null);
  // const [disableHearingFields, setDisableHearingFields] = useState(false);
  const [disableHearingFileFields, setDisableHearingFileFields] =
    useState(false);
  const [hearingSourceId, setHearingSourceId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [showModalConfirmation, setShowModalConfirmation] =
    useState<boolean>(false);

  const [attendantId, setAttendantId] = useState<number | null>();
  const [attendantList, setAttendantList] = useState<any[]>([]);
  const [selectedCustomerAttendantId, setSelectedCustomerAttendantId] =
    useState<number | undefined>();
  const [selectedCustomerAttendantName, setSelectedCustomerAttendantName] =
    useState("");
  const [fieldError, setFieldError] = useState<boolean>(false);
  const [fieldErrorCreate, setFielErrorCreate] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [attendantRoleId, setAttendantRoleId] = useState<number>();
  const [attendantRoleIndex, setAttendantRoleIndex] = useState<number>();

  const handleSetAttendantRole = (id: number, index?: number) => {
    setAttendantRoleId(id);
    setAttendantRoleIndex(index);
  };
  const [, setValidateVideo] = useState(true);
  const [createAudienceMask, setCreateAudienceMask] = useState("");
  const [recordProviderId, setRecordProviderId] = useState<number | null>(null);
  const [recordMode, setRecordMode] = useState<string>("1");
  const [showCustomFieldErrors, setShowCustomFieldErrors] = useState(false);

  useEffect(() => {
    setShowCustomFieldErrors(false);
  }, [openModal]);

  const [customFields, setCustomFields] = useState<IHearingCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<number, string | boolean>
  >({});
  const [procedureCustomerSourceId, setProcedureCustomerSourceId] = useState<
    number | null
  >(null);
  const [procedureCustomerSourceName, setProcedureCustomerSourceName] =
    useState("");
  const [procedureCustomerSourceSelected, setProcedureCustomerSourceSelected] =
    useState<any>("");
  const [procedureCustomerSources, setProcedureCustomerSources] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [attendantFilterId, setAttendantFilterId] = useState<number | null>(
    null,
  );
  const [filtersLoaded, setFiltersLoaded] = useState<boolean>(false);
  const [errorFields, setErrorFields] = useState({
    nameAudience: false,
    hours: false,
    date: false,
    audienceType: false,
    recordMode: false,
    procedureCustomerSource: false,
  });

  const [listAttendantInHearingFile, setListAttendantInHearingFile] = useState<
    Array<IHearingAttendant>
  >([]);

  //==================================================================================================================== Downloads

  const [openDownloads, setOpenDownloads] = useState<boolean>(false);
  const [listDownloads, setListDownloads] = useState<
    Array<IHearingFileDownloads>
  >([]);

  const [openAttachmentDrawer, setOpenAttachmentDrawer] =
    useState<boolean>(false);

  //==================================================================================================================== Drag And Drop Validated File

  const [file, setFile] = useState<File | null>(editItem ? editItem : null);
  const [fileError, setFileError] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_typeError, setTypeError] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_type, setType] = useState<any>();

  //==================================================================================================================== Delete hearing File
  const [openModalConfirmDelete, setOpenModalConfirmDelete] =
    useState<boolean>(false);
  const [hearingFileIdToDelete, setHearingFileIdToDelete] = useState<number>(0);

  //==================================================================================================================== Edit Hearing File
  const [openEditRecord, setOpenEditRecord] = useState<boolean>(false);
  const [hearingFileToEdit, setHearingFileToEdit] = useState<
    IHearingFileBody | undefined
  >(undefined);

  const [dateHearingFile, setDateHearingFile] = useState<string | null>();
  const [timeHearingFile, setTimeHearingFile] = useState<string | undefined>();

  const [recordEditedReturn, setRecordEditedReturn] =
    useState<IHearingFileBody>();

  //==================================================================================================================== FILTER HEARING / AUDIENCE
  const [openHearingFilter, setOpenHearingFilter] = useState<boolean>(false);

  const savedFilter = useAppSelector((state) => state.audienceFilter);
  const isFilter = savedFilter.isFilterActive;

  const [hearingNumber, setHearingNumber] = useState<string>("");
  const [startDate, setStartDate] = useState<string | null | undefined>("");
  const [finishDate, setFinishDate] = useState<string | null | undefined>("");
  const [activeFilter, setActiveFilter] = useState<boolean | null>(true);
  const [valueOptionDropDown, setValueOptionDropDown] = useState<any>();
  const [clearDatesFilter, setClearDatesFilter] = useState<number>(0);
  const [startDateError, setStartDateError] = useState<boolean>(false);
  const [finishDateError, setFinishDateError] = useState<boolean>(false);
  const [isCountyFilter, setIsCountyFilter] = useState<boolean>(false);
  const [hearingTypeIdFilter, setHearingTypeIdFilter] = useState<number | null>(
    null,
  );
  const [hearingSourceIdFilter, setHearingSourceIdFilter] = useState<
    number | null
  >(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [applyReadOnlyOnSearchTerm, setApplyReadOnlyOnSearchTerm] =
    useState(false);
  const [shouldConnect, setShouldConnect] = useState(false);
  const query = useQuery();

  const options: Array<IOptionsFilterStatus> = [
    { label: t("audiences.status.scheduled"), value: "A" },
    { label: t("audiences.status.completed"), value: "E" },
    { label: t("audiences.status.inProgress"), value: "G" },
    { label: t("audiences.status.inconsistent"), value: "O" },
    { label: t("audiences.status.error"), value: "R" },
    { label: t("audiences.status.cancelled"), value: "C" },
    { label: t("audiences.status.processing"), value: "S" },
  ];

  //====================================================================================================================

  const navigate = useNavigate();
  const location = useLocation();

  const dataLocation = location.state;

  async function requestPermission() {
    return permissions.camera && permissions.mic;
  }

  function clearAllCookies() {
    const cookies = document.cookie.split(";");

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }

    if ("caches" in window) {
      caches.keys().then(function (cacheNames) {
        cacheNames.forEach(function (cacheName) {
          caches.delete(cacheName);
        });
      });
    }

    const cookies2 = document.cookie.split(";");
    for (let i = 0; i < cookies2.length; i++) {
      const cookie = cookies2[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    }
  }

  const validateRecordingPreconditions = async (): Promise<boolean> => {
    const roomLimit = customerCertificate?.roomLimit;
    if (roomLimit && roomLimit > 0) {
      const response = await hearingFileServiceV2.getActiveRoom();
      const totalActiveRooms = response?.body || 0;
      if (totalActiveRooms >= roomLimit) {
        addToast({
          type: "warning",
          title: t("recordings.messages.roomLimitTitle"),
          description: t("recordings.messages.roomLimitDescription"),
        });
        return false;
      }
    }

    if (listAttendantInHearingFile.length <= 0) {
      addToast({
        type: "warning",
        title: t("participants.messages.requiredTitle"),
        description: t("participants.messages.requiredDescription"),
      });
      return false;
    }

    const temPermissaoDeCamera = await requestPermission();
    if (!temPermissaoDeCamera) {
      addToast({
        type: "warning",
        title: t("recordings.messages.cameraRequired"),
        description: t("recordings.messages.cameraRequiredDescription"),
      });
      return false;
    }

    return true;
  };

  const createHearingRecord = async (item: any) => {
    setLoading(true);
    try {
      const isValid = await validateRecordingPreconditions();
      if (!isValid) return;

      if (!accessToken || !audience || !audience.id) return;

      clearAllCookies();

      const RECORD_MODE_LOCAL = 2;
      const PROVIDER_MCONF = 2;

      if (audience.recordMode === RECORD_MODE_LOCAL) {
        navigate(
          `/recording/local?hearingId=${item?.hearingId}&hearingFileId=${item?.id}`,
          {
            state: {
              procedureNumber: audience?.procedureNumber,
              procedureCustomerSourceName:
                audience?.procedureCustomerSourceName,
            },
          },
        );
        return;
      }

      if (recordProviderId === PROVIDER_MCONF) {
        const joinResp = await MeetingMConfService.createMeeting(item.id);
        if (!joinResp.status) {
          addToast({
            type: "error",
            title: "Provedor de gravação",
            description:
              joinResp.message ?? t("recordings.messages.mconfError"),
          });
          return;
        }

        navigate(
          `/recording/mconf?hearingId=${item?.hearingId}&hearingFileId=${item?.id}&attendantId=${item.attendantId || ""}`,
          {
            state: {
              procedureNumber: audience?.procedureNumber,
              procedureCustomerSourceName:
                audience?.procedureCustomerSourceName,
              mconfJoinUrl: joinResp.data?.message ?? "",
            },
          },
        );
        return;
      }

      const response = await HearingService.openHearingRecord(
        accessToken,
        item.id,
      );

      if (typeof response?.data?.message === "string") {
        const deviceId = response.data.message.split("&")[0];
        localStorage.setItem("deviceId", deviceId);
      }

      if (response?.status) {
        hearingFileService.patch("userId", item?.id, user?.id);

        document.cookie = `${response?.data?.token}`;
        document.cookie = `${response?.data?.sessionToken}`;

        navigate(
          `/recording/una?hearingId=${item?.hearingId}&hearingFileId=${item?.id}`,
          {
            state: {
              ...audience,
              hearingId: item?.id,
              urlModerator: response?.data?.urlModerator,
              urlAttendee: response?.data?.urlAttendee,
              videoRoomId: response?.data?.videoRoom?.id,
              authToken: response?.data?.token,
              deviceToken: response?.data?.sessionToken,
            },
          },
        );
      } else {
        addToast({
          type: "error",
          title: t("recordings.messages.cannotRecord"),
          description: t("recordings.messages.checkAudience"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  function formatFileSize(bytes: number) {
    if (bytes < 1024) {
      return bytes + " bytes";
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(2) + " KB";
    } else if (bytes < 1024 * 1024 * 1024) {
      return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    } else if (bytes < 1024 * 1024 * 1024 * 1024) {
      return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
    } else {
      return (bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2) + " TB";
    }
  }

  const changeRecord = async (selected: any) => {
    if (isConfidentialHearing && !managerHearingClassified) {
      return addToast({
        type: "warning",
        title: t("audiences.messages.cannotViewRecording"),
        description: t("audiences.messages.confidentialWarning"),
      });
    }
    if (record?.id === selected?.id && record?.status === selected?.status) {
      return setRecord(null);
    }

    setRecord(null);
    setTimeout(() => {
      setRecord(selected);
    }, 5);
  };

  useEffect(() => {
    setRecord((currentRecord: any) => {
      if (!currentRecord?.id) return currentRecord;

      const updatedRecord = listRecord?.hearingInProgress?.find(
        (item: any) => item.id === currentRecord.id,
      );

      return updatedRecord ?? currentRecord;
    });
  }, [listRecord]);

  useEffect(() => {
    if (user) {
      SystemConfigurationService.getAllByCustomer(user?.customerId).then(
        (response) => {
          if (!Array.isArray(response?.data)) return;

          const SystemConfigurationFilter = response?.data?.filter(
            (item: any) =>
              item?.name === "ProcedureMask" || item?.name === "RecordProvider",
          );

          const maskConfig = SystemConfigurationFilter.find(
            (i: any) => i?.name === "ProcedureMask",
          );

          if (maskConfig) {
            setCreateAudienceMask(maskConfig.value);
          } else {
            setCreateAudienceMask("");
          }

          const recordProviderConfig = SystemConfigurationFilter.find(
            (i: any) => i?.name === "RecordProvider",
          );
          if (recordProviderConfig) {
            const providerId = parseInt(recordProviderConfig.value, 10);
            setRecordProviderId(providerId);
          }
        },
      );

      HearingCustomFieldService.getAll(user?.customerId, 1).then((fields) => {
        setCustomFields(fields);

        const initialValues: Record<number, string | boolean> = {};
        fields.forEach((field) => {
          if (field.defaultValue !== null && field.defaultValue !== undefined) {
            if (field.type === "bool") {
              initialValues[field.id] = field.defaultValue === "true";
            } else {
              initialValues[field.id] = field.defaultValue;
            }
          }
        });
        setCustomFieldValues(initialValues);
      });
    }
  }, []);

  useEffect(() => {
    if (!isInqueritoProduct || !user?.customerId) {
      setProcedureCustomerSources([]);
      return;
    }

    (async () => {
      const response = await ProcedureCustomerSourceService.getAll(
        user.customerId,
        1,
      );

      const sources = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      const normalizedSources = sources
        .filter((item: any) => item?.active !== false)
        .map((item: any) => ({
          id: item.id,
          name: item.name,
        }));

      setProcedureCustomerSources(normalizedSources);
    })();
  }, [isInqueritoProduct, user?.customerId]);

  useEffect(() => {
    if (!isInqueritoProduct || !openModal || !procedureCustomerSources.length) {
      return;
    }

    const selectedSource = procedureCustomerSources.find((source) => {
      if (
        procedureCustomerSourceId !== null &&
        Number(source.id) === Number(procedureCustomerSourceId)
      ) {
        return true;
      }

      return (
        !!procedureCustomerSourceName &&
        source.name === procedureCustomerSourceName
      );
    });

    if (!selectedSource) {
      return;
    }

    setProcedureCustomerSourceSelected(selectedSource);

    if (!procedureCustomerSourceName) {
      setProcedureCustomerSourceName(selectedSource.name);
    }

    if (procedureCustomerSourceId === null) {
      setProcedureCustomerSourceId(Number(selectedSource.id));
    }
  }, [
    isInqueritoProduct,
    openModal,
    procedureCustomerSourceId,
    procedureCustomerSourceName,
    procedureCustomerSources,
  ]);

  useEffect(() => {
    setRecord(null);
    setListRecord(null);
    setConfidentialityLevel(selectedAudienceConfidentialityLevel);
    setIsClassifiedState(selectedAudienceIsClassified);
  }, [
    audience?.id,
    selectedAudienceConfidentialityLevel,
    selectedAudienceIsClassified,
  ]);

  const formatExpectedDate = (date: any, hours: number | string) => {
    const formattedDate = date.toISOString().split("T")[0];
    const formattedTime =
      typeof hours === "string" && hours.includes(":")
        ? `${hours}:00`
        : `${String(hours).padStart(2, "0")}:00:00`;
    return `${formattedDate}T${formattedTime}`;
  };

  useEffect(() => {
    const validNameAudience = isValidProcedureNumber(
      nameAudience,
      createAudienceMask,
    );

    setErrorFields((prevState) => ({
      ...prevState,
      nameAudience: !validNameAudience,
    }));
  }, [nameAudience, createAudienceMask]);

  const editAudience = async () => {
    setShowCustomFieldErrors(true);
    try {
      const userId: any = user ? user.id : null;

      const validNameAudience = isValidProcedureNumber(
        nameAudience,
        createAudienceMask,
      );
      if (
        !nameAudience ||
        !date ||
        (!isInqueritoProduct && !hours) ||
        !audienceType ||
        !recordMode ||
        (isInqueritoProduct &&
          !procedureCustomerSourceId &&
          !procedureCustomerSourceName.trim()) ||
        !validNameAudience ||
        Object.values(
          getRequiredCustomFieldErrors(customFields, customFieldValues),
        ).some(Boolean)
      ) {
        validFields(
          !validNameAudience ? null : nameAudience,
          date,
          hours,
          audienceType,
          recordMode,
          procedureCustomerSourceId,
          procedureCustomerSourceName,
        );
        addToast({
          type: "warning",
          title: t("audiences.messages.fillFields"),
          description: t("audiences.messages.fillFieldsDescription"),
        });
        return dispatch(hideLoading());
      }

      const selectedDate = new Date(date);
      const minimumYear = new Date("2000").getFullYear();
      if (selectedDate.getFullYear() < minimumYear) {
        addToast({
          type: "warning",
          title: t("audiences.messages.invalidDate"),
          description: t("audiences.messages.invalidDateDescription"),
        });
        return;
      }

      const bodyData = {
        guid: audience.guid,
        id: audience?.id,
        userId: userId,
        active: active,
        placeId: userCustomerProfile?.placeId,
        hearingTypeId: audienceType,
        hearingSourceId: isInqueritoProduct
          ? procedureCustomerSourceId || undefined
          : (hearingSourceId ?? null),
        description: audienceDescription,
        ...(usesBooleanConfidentiality
          ? {
              isClassified,
            }
          : { confidentialityLevel }),
        expectedDate:
          date && (isInqueritoProduct || hours)
            ? formatExpectedDate(
                new Date(date),
                isInqueritoProduct ? 0 : hours!,
              )
            : null,
        procedureNumber: nameAudience,
        recordMode: Number(recordMode),
        status: audience.status,
        device: LogOperation.getDevice(),
        ip: LogOperation.getMachineIP(),
        logPoliceUnitId: LogOperation.getPoliceUnitId(),
        logUserId: LogOperation.getUserId(),
      };

      setLoading(true);

      const hasCustomFieldValues = Object.keys(customFieldValues).some(
        (key) => {
          const value = customFieldValues[Number(key)];
          return value !== "" && value !== false && value !== undefined;
        },
      );

      let result: { status: boolean; message?: string };

      if (hasCustomFieldValues && customFields.length > 0) {
        const customFieldsPayload = customFields
          .filter((field) => {
            const value = customFieldValues[field.id];
            if (field.component === "RadioButton") {
              return true;
            }
            return value !== "" && value !== undefined;
          })
          .map((field) => {
            const existingField = audience.customFields?.find(
              (cf: any) => cf.hearingCustomFieldId === field.id,
            );
            return {
              id: existingField?.id || 0,
              hearingCustomFieldId: field.id,
              value: String(customFieldValues[field.id] ?? false),
              device: LogOperation.getDevice(),
              ip: LogOperation.getMachineIP(),
              logPoliceUnitId: LogOperation.getPoliceUnitId(),
              logUserId: LogOperation.getUserId(),
            };
          });

        result = await HearingService.putNewAudienceCustom(
          bodyData,
          customFieldsPayload,
        );
      } else {
        const response = await HearingService.putNewAudience(bodyData);
        result = { status: response.status === 200 || response.status === 204 };
      }

      if (!result.status) {
        setLoading(false);
        dispatch(hideLoading());
        addToast({
          type: "error",
          title: t("audiences.messages.updateError"),
          description: result.message,
        });
        return;
      }

      await updateAudiences({ audienceId: audience.id, notClose: true });
      setOpenModal(false);
      setIsEdit(false);
      setErrorFields({
        date: false,
        hours: false,
        nameAudience: false,
        audienceType: false,
        recordMode: false,
        procedureCustomerSource: false,
      });
      dispatch(hideLoading());
      setLoading(false);

      addToast({
        type: "success",
        title: t("audiences.messages.updated"),
      });
    } catch (error) {
      setLoading(false);
      dispatch(hideLoading());
      addToast({
        type: "error",
        title: t("audiences.messages.updateError"),
      });
    }
  };

  const validFields = (
    nameAudience: string | null,
    date: string | null,
    hours: number | null,
    audienceType: any,
    recordMode: string | null,
    procedureCustomerSourceId?: number | null,
    procedureCustomerSourceName?: string,
  ) => {
    setErrorFields({
      nameAudience: !nameAudience,
      date: !date,
      hours: !isInqueritoProduct && !hours,
      audienceType: !audienceType,
      recordMode: !recordMode,
      procedureCustomerSource:
        isInqueritoProduct &&
        !procedureCustomerSourceId &&
        !procedureCustomerSourceName?.trim(),
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isSubmitting) return;

      setIsSubmitting(true);
      await createAudience();

      setIsSubmitting(false);
      setLoading(false);
    } catch {
      setLoading(true);
      setIsSubmitting(false);
    }
  };

  const fetchParticipants = async (query: string) => {
    if (!query.trim()) {
      setAttendantList([]);
      return;
    }

    if (!user) return;

    try {
      const { data } =
        await procedureCustomerAttendantService.GetAllByNameOrDocument({
          customerId: user?.customerId,
          searchTerm: query,
          globalSearch: true,
        });
      const list = data?.data?.map((item: IParticipant) => {
        return {
          label: `${item.name ?? ""} - Documento: ${item.document || "N/A"}`,
          value: item.id,
        };
      });
      setAttendantList(list);
    } catch (error) {
      setAttendantList([]);
      console.error("Erro ao buscar participantes:", error);
    }
  };

  const fetchServerLinkOptions = useCallback(
    async (searchTerm: string) => {
      const requestId = ++serverLinkSearchRequestRef.current;

      try {
        setLoadingServerLinkOptions(true);
        const response =
          await AttendantService.getAllUserCustomerByCpfEmailOrName(searchTerm);
        const responseData = response?.data?.data ?? response?.data;
        const usersList = Array.isArray(responseData) ? responseData : [];
        const linkedUserCustomerIds = new Set(
          linkedServers.map((server) => server.userCustomerId).filter(Boolean),
        );
        const linkedEmails = new Set(
          linkedServers.map((server) => normalizeServerLinkEmail(server.email)),
        );
        const linkedCpfs = new Set(
          linkedServers.map((server) => normalizeServerLinkCpf(server.cpf)),
        );
        const mappedOptions = usersList
          .map(mapUserToServerLinkOption)
          .filter((option) => {
            const optionEmail = normalizeServerLinkEmail(option.email);
            const optionCpf = normalizeServerLinkCpf(option.cpf);

            return (
              Boolean(option.id && option.label) &&
              !linkedUserCustomerIds.has(option.userCustomerId) &&
              (!optionEmail || !linkedEmails.has(optionEmail)) &&
              (!optionCpf || !linkedCpfs.has(optionCpf))
            );
          });

        if (requestId === serverLinkSearchRequestRef.current) {
          setServerLinkOptions(mappedOptions);
        }
      } catch {
        if (requestId === serverLinkSearchRequestRef.current) {
          setServerLinkOptions([]);
          addToast({
            type: "error",
            title: t("audiences.serverLink.title"),
            description: t("audiences.serverLink.fetchError"),
          });
        }
      } finally {
        if (requestId === serverLinkSearchRequestRef.current) {
          setLoadingServerLinkOptions(false);
        }
      }
    },
    [addToast, linkedServers, t],
  );

  const fetchLinkedServersByHearingId = useCallback(async () => {
    if (!audience?.id) return;

    try {
      const response =
        await HearingService.getAllUserCustomerConfidentialityByHearingId(
          Number(audience.id),
        );
      const responseData = response?.data?.data ?? response?.data;
      const linksList = Array.isArray(responseData) ? responseData : [];
      const mappedLinks = linksList
        .map((item) => ({
          ...mapUserToServerLinkOption(item),
          persisted: true,
        }))
        .filter((option) => Boolean(option.id && option.label));

      setLinkedServers(mappedLinks);
    } catch {
      setLinkedServers([]);
      addToast({
        type: "error",
        title: t("audiences.serverLink.title"),
        description: t("audiences.serverLink.fetchLinkedError"),
      });
    }
  }, [addToast, audience?.id, t]);

  const handleSelectServerLink = (server: IServerLinkOption | null) => {
    if (!server) return;

    setServerLinksToCreate((currentServers) => {
      const alreadyLinked = linkedServers.some(
        (linkedServer) => linkedServer.id === server.id,
      );
      const alreadySelected = currentServers.some(
        (selectedServer) => selectedServer.id === server.id,
      );

      if (alreadyLinked || alreadySelected) return currentServers;
      return [...currentServers, server];
    });
    setServerLinkInput("");
    setServerLinkFieldError(false);
  };

  const handleRemoveServerLink = async (server: IServerLinkOption) => {
    if (server.persisted) {
      if (!server.confidentialityId) {
        addToast({
          type: "warning",
          title: t("audiences.serverLink.title"),
          description: t("audiences.serverLink.missingLink"),
        });
        return;
      }

      try {
        setLoading(true);
        await HearingService.deleteUserCustomerConfidentiality(
          server.confidentialityId,
        );
        addToast({
          type: "success",
          title: t("audiences.serverLink.title"),
          description: t("audiences.serverLink.removed"),
        });
        await fetchLinkedServersByHearingId();
      } catch {
        addToast({
          type: "error",
          title: t("audiences.serverLink.title"),
          description: t("audiences.serverLink.removeError"),
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    setServerLinksToCreate((currentServers) =>
      currentServers.filter((currentServer) => currentServer.id !== server.id),
    );
  };

  const handleCloseServerLinkDrawer = () => {
    serverLinkSearchRequestRef.current += 1;
    setOpenServerLinkDrawer(false);
    setServerLinkInput("");
    setServerLinkOptions([]);
    setLinkedServers([]);
    setServerLinksToCreate([]);
    setLoadingServerLinkOptions(false);
    setServerLinkFieldError(false);
  };

  const resolveServerLinkUserCustomerIds = async (
    servers: IServerLinkOption[],
  ) => {
    const selectedWithIds = servers
      .map((server) => server.userCustomerId)
      .filter(
        (userCustomerId): userCustomerId is number =>
          typeof userCustomerId === "number" && userCustomerId > 0,
      );

    if (selectedWithIds.length === servers.length) {
      return Array.from(new Set(selectedWithIds));
    }

    if (!user?.customerId) {
      return selectedWithIds;
    }

    const response = await AttendantService.getAllUserCoustomer(
      user.customerId,
      1,
    );
    const responseData = response?.data?.data;
    const usersList = Array.isArray(responseData) ? responseData : [];
    const mappedUsers = usersList.map(mapUserToServerLinkOption);

    const resolvedIds = servers
      .map((server) => {
        if (server.userCustomerId) {
          return server.userCustomerId;
        }

        const serverEmail = normalizeServerLinkEmail(server.email);
        const serverCpf = normalizeServerLinkCpf(server.cpf);
        const matchedUser = mappedUsers.find((userOption) => {
          const userEmail = normalizeServerLinkEmail(userOption.email);
          const userCpf = normalizeServerLinkCpf(userOption.cpf);

          return (
            (serverEmail && userEmail === serverEmail) ||
            (serverCpf && userCpf === serverCpf)
          );
        });

        return matchedUser?.userCustomerId;
      })
      .filter(
        (userCustomerId): userCustomerId is number =>
          typeof userCustomerId === "number" && userCustomerId > 0,
      );

    return Array.from(new Set(resolvedIds));
  };

  const handleApplyServerLink = async () => {
    if (!serverLinksToCreate.length) {
      setServerLinkFieldError(true);
      addToast({
        type: "warning",
        title: t("audiences.serverLink.title"),
        description: t("audiences.serverLink.selectRequired"),
      });
      return;
    }

    if (!audience?.id) {
      addToast({
        type: "warning",
        title: t("audiences.serverLink.title"),
        description: t("audiences.serverLink.missingHearing"),
      });
      return;
    }

    try {
      setLoading(true);
      const userCustomerIds =
        await resolveServerLinkUserCustomerIds(serverLinksToCreate);

      if (userCustomerIds.length !== serverLinksToCreate.length) {
        addToast({
          type: "warning",
          title: t("audiences.serverLink.title"),
          description: t("audiences.serverLink.unresolvedUsers"),
        });
        return;
      }

      await Promise.all(
        userCustomerIds.map((userCustomerId) =>
          HearingService.addUserCustomerConfidentiality({
            hearingId: Number(audience.id),
            userCustomerId,
          }),
        ),
      );

      addToast({
        type: "success",
        title: t("audiences.serverLink.title"),
        description: t("audiences.serverLink.linked"),
      });
      setServerLinksToCreate([]);
      setServerLinkInput("");
      setServerLinkOptions([]);
      await fetchLinkedServersByHearingId();
    } catch {
      addToast({
        type: "error",
        title: t("audiences.serverLink.title"),
        description: t("audiences.serverLink.linkError"),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!openServerLinkDrawer) return;

    fetchLinkedServersByHearingId();
  }, [fetchLinkedServersByHearingId, openServerLinkDrawer]);

  useEffect(() => {
    if (!openServerLinkDrawer) return;

    const searchTerm = normalizeServerLinkSearchTerm(serverLinkInput);
    if (!searchTerm) {
      serverLinkSearchRequestRef.current += 1;
      setServerLinkOptions([]);
      setLoadingServerLinkOptions(false);
      return;
    }

    const timeout = setTimeout(() => {
      fetchServerLinkOptions(searchTerm);
    }, 500);

    return () => clearTimeout(timeout);
  }, [fetchServerLinkOptions, openServerLinkDrawer, serverLinkInput]);

  useEffect(() => {
    setFieldError(false);
  }, [nameAttendant, attendantRoleId]);

  const onClickDesvincule = async () => {
    if (audience?.status === "E" && !(isAdmin && hearingAttendantUnlink)) {
      setShowModalConfirmation(false);
      return;
    }

    if (selectedCustomerAttendantId) {
      setShowModalConfirmation(false);
      setLoading(true);
      const { success, body } =
        await procedureCustomerAttendantService.unbinding(
          selectedCustomerAttendantId,
        );
      setLoading(false);
      if (success && body) {
        addToast({
          title: t("participants.messages.unlinked"),
          description: body,
          type: "success",
        });

        const newListAttendantInHearingFile =
          listAttendantInHearingFile?.filter(
            (x: any) => x?.id !== selectedCustomerAttendantId,
          );
        setListAttendantInHearingFile(newListAttendantInHearingFile);
      }

      if (success && !body) {
        addToast({
          title: t("participants.messages.unlinkError"),
          description: body,
          type: "warning",
        });

        clear();
      }

      if (!success) {
        addToast({
          title: t("participants.messages.unlinkError"),
          description: body,
          type: "error",
        });

        clear();
      }
    }
  };

  const onVinculeAttendat = async () => {
    try {
      if (!attendantId) {
        addToast({
          type: "warning",
          title: t("participants.messages.selectRequired"),
          description: t("participants.messages.selectRequiredDescription"),
        });
        setFieldError(true);
        return;
      }

      if (!attendantRoleId) {
        addToast({
          type: "warning",
          title: t("audiences.messages.fillFields"),
          description: t("audiences.messages.fillFieldsDescription"),
        });

        setFieldError(true);
        return;
      }

      // dispatch(showLoading());
      setLoading(true);

      const { status } = await AttendandoViculeService.vincule({
        attendantRoleId: attendantRoleId,
        attendantId: attendantId,
        hearingId: audience?.id,
        device: LogOperation.getDevice(),
        ip: LogOperation.getMachineIP(),
        logPoliceUnitId: userCustomerProfile?.id,
        logUserId: LogOperation.getUserId(),
        token: "",
        link: "",
        id: 0,
        used: 0,
        attempts: 0,
      });

      if (status) {
        addToast({
          type: "success",
          title: t("participants.link"),
          description: t("participants.messages.linked"),
        });

        setOpenDrawer(false);
        // setLoading(false);
        setAttendantId(null);
        setAttendantRoleId(undefined);
        return;
      }

      addToast({
        type: "warning",
        title: t("participants.link"),
        description: t("participants.messages.alreadyLinked"),
      });
      setFieldError(true);
      setAttendantId(null);
    } catch {
      addToast({
        type: "error",
        title: t("participants.messages.linkError"),
      });
      setAttendantId(null);
    } finally {
      setLoading(false);
    }
  };

  const createAudience = async () => {
    setShowCustomFieldErrors(true);
    try {
      const validNameAudience = isValidProcedureNumber(
        nameAudience,
        createAudienceMask,
      );
      if (
        !nameAudience ||
        !date ||
        (!isInqueritoProduct && !hours) ||
        !audienceType ||
        !recordMode ||
        (isInqueritoProduct &&
          !procedureCustomerSourceId &&
          !procedureCustomerSourceName.trim()) ||
        !validNameAudience ||
        Object.values(
          getRequiredCustomFieldErrors(customFields, customFieldValues),
        ).some(Boolean)
      ) {
        validFields(
          !validNameAudience ? null : nameAudience,
          date,
          hours,
          audienceType,
          recordMode,
          procedureCustomerSourceId,
          procedureCustomerSourceName,
        );
        addToast({
          type: "warning",
          title: t("audiences.messages.fillFields"),
          description: t("audiences.messages.fillFieldsDescription"),
        });
        return dispatch(hideLoading());
      }

      const selectedDate = new Date(date);
      const currentYear = new Date().getFullYear();
      if (selectedDate.getFullYear() < currentYear) {
        addToast({
          type: "warning",
          title: t("audiences.messages.invalidDate"),
          description: t("audiences.messages.invalidDateDescription"),
        });
        return;
      }

      const payload = {
        guid: uuidv4(),
        userId: user?.id,
        active: true,
        placeId: userCustomerProfile?.placeId,
        hearingTypeId: audienceType,
        hearingSourceId: isInqueritoProduct
          ? procedureCustomerSourceId || undefined
          : (hearingSourceId ?? null),
        ...(usesBooleanConfidentiality
          ? {
              isClassified,
            }
          : { confidentialityLevel }),
        expectedDate:
          date && (isInqueritoProduct || hours)
            ? formatExpectedDate(
                new Date(date),
                isInqueritoProduct ? 0 : hours!,
              )
            : null,
        procedureNumber: nameAudience,
        status: "A",
        recordMode: Number(recordMode),
        device: LogOperation.getDevice(),
        ip: LogOperation.getMachineIP(),
        logPoliceUnitId: LogOperation.getPoliceUnitId(),
        logUserId: LogOperation.getUserId(),
        description: audienceDescription || "",
      };

      const hasCustomFieldValues = Object.keys(customFieldValues).some(
        (key) => {
          const value = customFieldValues[Number(key)];
          return value !== "" && value !== false && value !== undefined;
        },
      );

      let data, status, message;

      if (hasCustomFieldValues && customFields.length > 0) {
        const customFieldsPayload = customFields
          .filter((field) => {
            const value = customFieldValues[field.id];
            if (field.component === "RadioButton") {
              return true;
            }
            return value !== "" && value !== undefined;
          })
          .map((field) => ({
            hearingCustomFieldId: field.id,
            value: String(customFieldValues[field.id] ?? false),
            device: LogOperation.getDevice(),
            ip: LogOperation.getMachineIP(),
            logPoliceUnitId: LogOperation.getPoliceUnitId(),
            logUserId: LogOperation.getUserId(),
          }));

        const result = await HearingService.postNewAudienceCustom(
          payload,
          customFieldsPayload,
        );
        data = result.data;
        status = result.status;
        message = result.message;
      } else {
        const result = await HearingService.postNewAudience(payload);
        data = result.data;
        status = result.status;
        message = result.message;
      }

      if (status) {
        setNameAudience("");
        await updateAudiences({ audienceId: data, notClose: true });
        setErrorFields({
          date: false,
          hours: false,
          nameAudience: false,
          audienceType: false,
          recordMode: false,
          procedureCustomerSource: false,
        });
        addToast({
          type: "success",
          title: t("audiences.messages.created"),
        });
        clear();
      } else {
        addToast({
          type: "warning",
          title: t("audiences.messages.createError"),
          description: message,
        });
      }
    } catch {
      addToast({
        type: "error",
        title: t("audiences.messages.createError"),
        description: t("errors.generic"),
      });
    }
  };

  const updateAudiences = async (props?: {
    audienceId?: number;
    notClose?: boolean;
    overrideFilters?: Partial<IFilterParameters>;
    page?: number;
  }): Promise<void> => {
    const requestId = ++audienceRequestIdRef.current;
    setLoading(true);
    try {
      // debugger;
      if (userCustomerProfile && userCustomerProfile?.placeId) {
        let params: any = props?.overrideFilters || {
          placeId: userCustomerProfile?.placeId,
          userId: viewOnlyYourRecords ? user?.id : undefined,
        };

        if (!props?.overrideFilters) {
          if (isFilter) {
            params = {
              placeId: userCustomerProfile?.placeId,
              district: isCountyFilter,
              procedureNumber: hearingNumber || undefined,
              startDate: startDate || undefined,
              finishDate: finishDate || undefined,
              status: statusFilter || undefined,
              ...(activeFilter !== null ? { active: activeFilter } : {}),
              hearingTypeId: hearingTypeIdFilter || undefined,
              hearingSourceId: hearingSourceIdFilter || undefined,
              attendantId: attendantFilterId || undefined,
            };
          } else {
            if (startDate && finishDate) {
              params.startDate = startDate;
              params.finishDate = finishDate;
            }
          }
        } else {
          if (!params.placeId) {
            params.placeId = userCustomerProfile?.placeId;
            params.userId = viewOnlyYourRecords ? user?.id : undefined;
          }
        }

        const requestedPage = props?.overrideFilters
          ? 0
          : (props?.page ?? currentAudiencePage);
        if (props?.overrideFilters) {
          setCurrentAudiencePage(0);
        }
        params.pageNumber = requestedPage + 1;
        params.pageSize = audiencePageSize;
        if (props?.overrideFilters) {
          webSocketRequestQueueRef.current = [];
          ignoreWebSocketUntilRef.current = Date.now() + 12000;
          const currentRequestFilters = { ...params };
          getHearingFiltersRef.current = () => currentRequestFilters;
        }

        const response = await audiencesServices.getAllByFiltersV2(params);
        if (requestId !== audienceRequestIdRef.current) return;
        if (response.success) {
          const responseData = response.data || [];
          setTotalAudiences(response.totalCount ?? responseData.length);

          if (props?.audienceId) {
            responseData.forEach((element: any) => {
              if (element?.id === props?.audienceId) {
                setAudience(element);
              }
            });
          }

          setAudiences(responseData);
          const hearingId = query.get("hearingId");

          if (hearingId) {
            if (hearingId) {
              const audienceFinded = responseData?.find(
                (x: any) => x.id == hearingId,
              );
              setAudience(audienceFinded);
            }
          }
        } else {
          if (response.status === 404) {
            setAudiences([]);
            setTotalAudiences(0);
            setCurrentAudiencePage(0);
            setAudience(null);
          }
          console.error("Erro ao atualizar audiências:", response.data);
        }
      }
    } catch (error) {
      if (requestId === audienceRequestIdRef.current) {
        console.error("Erro ao atualizar audiências:", error);
      }
    }

    if (requestId === audienceRequestIdRef.current) {
      setLoading(false);
    }
  };

  const handleAudiencePageChange = (page: number) => {
    setCurrentAudiencePage(page);
    setAudience(null);
    updateAudiences({ page });
  };

  useEffect(() => {
    if (savedFilter.isFilterActive) {
      setHearingNumber(savedFilter.hearingNumber || "");
      setStartDate(savedFilter.startDate || "");
      setFinishDate(savedFilter.finishDate || "");
      setActiveFilter(savedFilter.activeFilter ?? true);
      setValueOptionDropDown(savedFilter.valueOptionDropDown);
      setIsCountyFilter(savedFilter.isCountyFilter ?? false);
      setHearingTypeIdFilter(savedFilter.hearingTypeIdFilter || null);
      setHearingSourceIdFilter(savedFilter.hearingSourceIdFilter || null);
      setStatusFilter(savedFilter.statusFilter || null);
      setAttendantFilterId(savedFilter.attendantFilterId || null);
      setFiltersLoaded(true);
    } else {
      setFiltersLoaded(true);
    }
  }, [
    savedFilter.isFilterActive,
    savedFilter.hearingNumber,
    savedFilter.startDate,
    savedFilter.finishDate,
    savedFilter.activeFilter,
    savedFilter.valueOptionDropDown,
    savedFilter.isCountyFilter,
    savedFilter.hearingTypeIdFilter,
    savedFilter.statusFilter,
    savedFilter.attendantFilterId,
  ]);

  const prevPlaceIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const currentPlaceId = userCustomerProfile?.placeId;
    const userId = viewOnlyYourRecords ? user?.id : undefined;
    if (!currentPlaceId) return;
    if (!filtersLoaded) return;

    const isFirstLoad = prevPlaceIdRef.current === undefined;
    const isProfileChange =
      !isFirstLoad && prevPlaceIdRef.current !== currentPlaceId;

    if (isFirstLoad || isProfileChange) {
      if (isProfileChange) {
        setAudience(null);
        setAudiences([]);
        setTotalAudiences(0);
        setCurrentAudiencePage(0);
        setHearingNumber("");
        setStartDate(null);
        setFinishDate(null);
        setActiveFilter(true);
        setIsCountyFilter(false);
        setHearingTypeIdFilter(null);
        setStatusFilter(null);
        setAttendantFilterId(null);
        dispatch(clearAudienceFilterAction());
        updateAudiences({
          overrideFilters: { placeId: currentPlaceId, userId },
        });
      } else if (savedFilter.isFilterActive) {
        updateAudiences({
          overrideFilters: {
            placeId: currentPlaceId,
            district: savedFilter.isCountyFilter ?? false,
            procedureNumber: savedFilter.hearingNumber || undefined,
            startDate: savedFilter.startDate || undefined,
            finishDate: savedFilter.finishDate || undefined,
            status: savedFilter.statusFilter || undefined,
            ...(savedFilter.activeFilter !== null
              ? { active: savedFilter.activeFilter }
              : {}),
            hearingTypeId: savedFilter.hearingTypeIdFilter || undefined,
            attendantId: savedFilter.attendantFilterId || undefined,
            userId,
          },
        });
      } else {
        updateAudiences();
      }
    }

    prevPlaceIdRef.current = currentPlaceId;
  }, [userCustomerProfile?.placeId, filtersLoaded]);

  const clearModal = () => {
    setOpenDrawer(false);
    setAttendantId(null);
    setAttendantRoleId(undefined);
    setAttendantRoleIndex(undefined);
    setFieldError(false);
    setAttendantList([]);
  };

  const getDefaultCustomFieldValues = () => {
    const initialValues: Record<number, string | boolean> = {};
    customFields.forEach((field) => {
      if (field.defaultValue !== null && field.defaultValue !== undefined) {
        if (field.type === "bool") {
          initialValues[field.id] = field.defaultValue === "true";
        } else {
          initialValues[field.id] = field.defaultValue;
        }
      }
    });
    return initialValues;
  };

  const clear = () => {
    setIsEdit(false);
    setActive(true);
    setOpenModal(false);
    setOpenModalNotify(false);
    setAudienceType("");
    setHearingSourceId(null);
    setNameAudience("");
    setDate("");
    setHours(0);
    setErrorFields({
      date: false,
      hours: false,
      nameAudience: false,
      audienceType: false,
      recordMode: false,
      procedureCustomerSource: false,
    });
    setEditTypeSelected("");
    setSelectedCustomerAttendantId(undefined);
    setSelectedCustomerAttendantName("");
    setNameAttendant("");
    setDocumentAttendant("");
    setEmailAttendant("");
    setFieldError(false);
    setFielErrorCreate(false);
    setShowModalConfirmation(false);
    setOpenModalCreate(false);
    setConfidentialityLevel(defaultConfidentialityLevel);
    setIsClassifiedState(false);
    setRecordMode("");
    setAudienceDescription("");
    setProcedureCustomerSourceId(null);
    setProcedureCustomerSourceName("");
    setProcedureCustomerSourceSelected("");
    setCustomFieldValues(getDefaultCustomFieldValues());
  };

  const handleProcedureCustomerSourceChange = (source: any) => {
    setProcedureCustomerSourceSelected(source);
    setProcedureCustomerSourceId(source?.id ? Number(source.id) : null);
    setProcedureCustomerSourceName(source?.name || "");
    setErrorFields((prevState) => ({
      ...prevState,
      procedureCustomerSource: false,
    }));
  };

  const handleCustomFieldChange = (
    fieldId: number,
    value: string | boolean,
  ) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const emptyName = stringFormat.isEmptyOrSpaces(nameAttendant);
  const emptyDocument = stringFormat.isEmptyOrSpaces(documentAttendant);
  const emptyEmail = stringFormat.isEmptyOrSpaces(emailAttendant);

  const createAttendant = async () => {
    const payload = {
      customerId: user?.customerId,
      active: active,
      name: nameAttendant,
      createIn: getActualTime(),
      updateIn: getActualTime(),
      document: documentAttendant,
      email: !emptyEmail ? emailAttendant : null,
    };

    if (emptyName || emptyDocument) {
      setFielErrorCreate(true);
      addToast({
        type: "warning",
        title: t("participants.singular"),
        description: t("participants.messages.fillFields"),
      });
      return;
    }
    if (
      emailAttendant?.trim()?.length &&
      !verifyEmailFormat(emailAttendant.toLowerCase())
    ) {
      addToast({
        type: "warning",
        title: t("forms.validations.email"),
        description: t("forms.validations.invalidEmail"),
      });
      setFieldError(true);
      return;
    }

    try {
      await AttendantService.postNewAttendant(payload);
      addToast({
        type: "success",
        title: t("participants.messages.created"),
        description: t("participants.messages.created"),
      });
      clear();
    } catch (error: any) {
      const message = error?.response?.data?.message;
      addToast({
        type: "error",
        title: t("participants.messages.createError"),
        description:
          message || t("participants.messages.createErrorDescription"),
      });
    }
  };

  const onSelectAttendantRemove = (id: number) => {
    if (audience?.status === "E" && !(isAdmin && hearingAttendantUnlink))
      return;

    setSelectedCustomerAttendantId(id);
    setSelectedCustomerAttendantName(
      listAttendantInHearingFile.find((attendant) => attendant.id === id)?.name ||
        "",
    );
    setShowModalConfirmation(true);
  };

  const openModalCleared = () => {
    clear();
    setOpenModal(true);
  };

  const openModalEdit = async () => {
    if (!audience) return;

    let audienceData = audience;

    if (!audience.customFields || audience.customFields.length === 0) {
      const response = await HearingService.getHearingById(audience.id);
      if (response.status && response.data) {
        const data = response.data.hearingScheduled;
        const findedAudience = data.find(
          (h: any) => h.hearingId === audience.id,
        );
        audienceData = { ...audience, findedAudience };
        setAudience(audienceData);
      }
    }

    const fillCustomFieldValues = () => {
      if (audienceData.customFields && audienceData.customFields.length > 0) {
        const savedValues: Record<number, string | boolean> = {};
        audienceData.customFields.forEach((cf: any) => {
          const fieldDef = customFields.find(
            (f) => f.id === cf.hearingCustomFieldId,
          );
          if (fieldDef?.type === "bool") {
            savedValues[cf.hearingCustomFieldId] = cf.value === "true";
          } else {
            savedValues[cf.hearingCustomFieldId] = cf.value;
          }
        });
        setCustomFieldValues(savedValues);
      } else {
        setCustomFieldValues(getDefaultCustomFieldValues());
      }
    };

    if (isInqueritoProduct) {
      setOpenModal(true);
      setIsEdit(true);

      const datePart = audienceData.expectedDate.split("T")[0];
      const timePart = audienceData.expectedDate
        .split("T")[1]
        .split(":")
        .slice(0, 2)
        .join(":");

      setDate(datePart);
      setHours(timePart as any);
      setAudienceType(audienceData.hearingTypeId);
      setHearingSourceId(audienceData.hearingSourceId ?? null);
      setEditTypeSelected(audienceData.hearingTypeName);
      setNameAudience(audienceData.procedureNumber);
      setRecordMode(audienceData.recordMode);
      setAudienceDescription(audienceData.description || "");
      setProcedureCustomerSourceId(
        audienceData.hearingSourceId ??
          audienceData.procedureCustomerSourceId ??
          null,
      );
      setProcedureCustomerSourceName(
        audienceData.procedureCustomerSourceName || "",
      );

      const selectedProcedureCustomerSource = procedureCustomerSources.find(
        (source) =>
          Number(source.id) ===
            Number(
              audienceData.hearingSourceId ??
                audienceData.procedureCustomerSourceId ??
                null,
            ) || source.name === audienceData.procedureCustomerSourceName,
      );

      setProcedureCustomerSourceSelected(selectedProcedureCustomerSource || "");
      fillCustomFieldValues();
      setActive(audienceData.active);
      return;
    }

    const expectedDate = String(
      audienceData.expectedDate ?? audienceData.ExpectedDate ?? "",
    );
    const [datePart = "", timeValue = ""] = expectedDate.split("T");
    const timePart = timeValue.split(":").slice(0, 2).join(":");
    const hearingSourceValue =
      audienceData.hearingSourceId ?? audienceData.HearingSourceId ?? null;
    const procedureCustomerSourceValue =
      audienceData.procedureCustomerSourceId ??
      audienceData.ProcedureCustomerSourceId ??
      null;
    const procedureCustomerSourceNameValue =
      audienceData.procedureCustomerSourceName ??
      audienceData.ProcedureCustomerSourceName ??
      "";

    setDate(datePart);
    setHours(timePart as any);
    setAudienceType(audienceData.hearingTypeId ?? audienceData.HearingTypeId);
    setHearingSourceId(hearingSourceValue);
    setEditTypeSelected(
      audienceData.hearingTypeName ?? audienceData.HearingTypeName,
    );
    setNameAudience(
      audienceData.procedureNumber ?? audienceData.ProcedureNumber,
    );
    setRecordMode(audienceData.recordMode ?? audienceData.RecordMode);
    setAudienceDescription(
      audienceData.description ?? audienceData.Description ?? "",
    );
    setProcedureCustomerSourceId(
      hearingSourceValue ?? procedureCustomerSourceValue ?? null,
    );
    setProcedureCustomerSourceName(procedureCustomerSourceNameValue);

    const selectedProcedureCustomerSource = procedureCustomerSources.find(
      (source) =>
        Number(source.id) ===
          Number(hearingSourceValue ?? procedureCustomerSourceValue ?? null) ||
        source.name === procedureCustomerSourceNameValue,
    );

    setProcedureCustomerSourceSelected(selectedProcedureCustomerSource || "");
    fillCustomFieldValues();
    setActive(audienceData.active ?? audienceData.Active);
    setConfidentialityLevel(
      normalizeConfidentialityLevel(
        getAudienceConfidentialityLevel(
          audienceData,
          usesBooleanConfidentiality,
        ),
      ),
    );
    setIsClassifiedState(
      usesBooleanConfidentiality &&
        normalizeConfidentialityLevel(
          audienceData?.isClassified ?? audienceData?.IsClassified,
        ) > 0,
    );
    setIsEdit(true);
    setOpenModal(true);
  };

  //========================================================================================================== DELETE HEARING FILE

  const handleClickDelete = (element: any) => {
    setOpenModalConfirmDelete(true);
    setHearingFileIdToDelete(element.id);
  };

  //========================================================================================================== EDIT HEARING FILE

  const handleClickEdit = (element: IHearingFileBody) => {
    setLoading(true);

    const hearingFileAux: IHearingFileBody = {
      userId: element.userId,
      hearingFileTypeId: element.hearingFileTypeId ?? null,
      confidential: element.confidential ?? false,
      attendantId: element.attendantId ?? null,
      createIn: element.createIn,
      download: element.download,
      duration: element.duration,
      expectedDate: new Date(element.expectedDate ?? ""),
      fileOrder: element.fileOrder,
      filename: element.filename,
      finishDate: element.finishDate,
      hash: element?.hash,
      hearingId: element.hearingId,
      id: element?.id,
      platform: element.platform,
      recordId: element.recordId,
      size: element.size,
      startDate: element.startDate,
      status: element.status,
      title: element.title,
      type: element.type,
      updateIn: new Date(),
      upload: element.upload,
      watermark: element.watermark,
      device: LogOperation.getDevice(),
      ip: LogOperation.getMachineIP(),
      logPoliceUnitId: LogOperation.getPoliceUnitId(),
      logUserId: LogOperation.getUserId(),
    };

    setHearingFileToEdit(hearingFileAux);

    if (hearingFileAux && hearingFileAux.expectedDate) {
      const newDate = format(
        hearingFileAux && hearingFileAux?.expectedDate,
        "yyyy-MM-dd",
      );
      const newTime = format(
        hearingFileAux && hearingFileAux?.expectedDate,
        "HH:mm",
      );
      setDateHearingFile(newDate);
      setTimeHearingFile(newTime.toString());
    }

    setOpenEditRecord(true);
    setLoading(false);
  };

  useEffect(() => {
    if (listRecord && recordEditedReturn !== undefined) {
      updateSchaduledList(
        recordEditedReturn,
        listRecord,
        setListRecord,
        addToast,
      );
    }
  }, [recordEditedReturn]);

  //==========================================================================================================
  const onClickSearch = async (p?: FilterExtras) => {
    if (startDate && !finishDate) {
      setFinishDateError(true);
      addToast({
        title: t("common.warning"),
        description: t("audiences.filter.fillBothDates"),
        type: "warning",
      });
      return;
    } else if (!startDate && finishDate) {
      setStartDateError(true);
      addToast({
        title: t("common.warning"),
        description: t("audiences.filter.fillBothDates"),
        type: "warning",
      });
      return;
    }

    if (startDate && finishDate && new Date(startDate) > new Date(finishDate)) {
      setStartDateError(true);
      setFinishDateError(true);
      addToast({
        title: t("common.warning"),
        description: t("audiences.filter.invalidPeriod"),
        type: "warning",
      });
      return;
    }

    const hasAnyFilter =
      (!!hearingNumber && hearingNumber.trim() !== "") ||
      !!startDate ||
      !!finishDate ||
      statusFilter != null ||
      hearingTypeIdFilter != null ||
      hearingSourceIdFilter != null ||
      isCountyFilter ||
      activeFilter !== null ||
      p?.attendantId != null;

    setApplyReadOnlyOnSearchTerm(Boolean(p?.attendantId));

    dispatch(
      setAudienceFilter({
        hearingNumber,
        startDate,
        finishDate,
        activeFilter,
        valueOptionDropDown,
        isCountyFilter,
        hearingTypeIdFilter,
        hearingSourceIdFilter,
        statusFilter,
        attendantFilterId: p?.attendantId ?? attendantFilterId,
        isFilterActive: hasAnyFilter,
        userId: viewOnlyYourRecords ? user?.id : undefined,
      }),
    );

    setStartDateError(false);
    setFinishDateError(false);
    setAudience(null);
    setOpenHearingFilter(false);

    const filters = hasAnyFilter
      ? {
          placeId: isCountyFilter
            ? userCustomerProfile?.codeDistrict
            : userCustomerProfile?.placeId,
          district: isCountyFilter,
          procedureNumber: hearingNumber || undefined,
          startDate: startDate || undefined,
          finishDate: finishDate || undefined,
          status: statusFilter || undefined,
          ...(activeFilter !== null ? { active: activeFilter } : {}),
          hearingTypeId: hearingTypeIdFilter || undefined,
          hearingSourceId: hearingSourceIdFilter || undefined,
          attendantId: (p?.attendantId ?? attendantFilterId) || undefined,
          userId: viewOnlyYourRecords ? user?.id : undefined,
        }
      : {
          placeId: isCountyFilter
            ? userCustomerProfile?.codeDistrict
            : userCustomerProfile?.placeId,
          userId: viewOnlyYourRecords ? user?.id : undefined,
        };

    await updateAudiences({
      overrideFilters: filters,
    });
  };

  const onClickClear = () => {
    setApplyReadOnlyOnSearchTerm(false);
    dispatch(clearAudienceFilterAction());

    setHearingNumber("");
    setStartDate(null);
    setFinishDate(null);
    setValueOptionDropDown(undefined);
    setActiveFilter(true);
    setClearDatesFilter(clearDatesFilter + 1);
    setStartDateError(false);
    setFinishDateError(false);
    setIsCountyFilter(false);
    setHearingTypeIdFilter(null);
    setHearingSourceIdFilter(null);
    setStatusFilter(null);
    setAttendantFilterId(null);
    setAudience(null);

    updateAudiences({
      overrideFilters: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        placeId: userCustomerProfile?.placeId!,
        userId: viewOnlyYourRecords ? user?.id : undefined,
      },
    });
  };
  const handleCloseModal = () => {
    setTypeError(false);
    setFileError(false);
    setOpenModalValidation(false);
    setOpenModalVinculeDataToAnotherHearing(false);
    setHearingToMigrate(null);
    setSwapModeToMigrate(1);
    setValidateVideo(true);
    setType(null);
    if (!loadingUpload) {
      setFile(null);
      setUploadingFile(false);
    }
  };

  const onCloseModal = () => {
    setHearingNumber("");
    setStartDate("");
    setFinishDate("");
    setValueOptionDropDown("");
    setOpenHearingFilter(false);
    setRecordMode("1");
    setAttendantFilterId(null);
  };

  const handleApplyValidationVideo = () => {
    if (!file) {
      addToast({
        type: "warning",
        title: t("validation.authenticityTitle"),
        description: t("validation.attachFile"),
      });
      return;
    }

    SystemConfigurationService.getVideoAuthenticityValidation(
      file?.name,
      audience?.procedureNumber,
    ).then((response) => {
      if (response && response?.messageCode === 105) {
        addToast({
          type: "success",
          title: t("validation.authenticityTitle"),
          description: response.message,
        });
      } else {
        addToast({
          type: "error",
          title: t("validation.authenticityTitle"),
          description: response.message,
        });
      }
      handleCloseModal();
    });
  };

  useEffect(() => {
    if (dataLocation) {
      setAudience(dataLocation);
    }
  }, [dataLocation]);

  useEffect(() => {
    if (file !== null) {
      setUploadingFile(true);
    } else {
      setUploadingFile(false);
    }
  }, [file, uploadingFile]);

  //========================================================================================================== NOTIFY

  const handleClickNotify = async () => {
    const hasNotify = await checkInputsFilled(
      addToast,
      setLoading,
      inputValues,
    );

    if (hasNotify) {
      updateStatusHearing(
        audience.id,
        audiences,
        "N",
        setAudiences,
        listRecord,
        setListRecord,
      );
      setOpenModalNotify(false);
    }
  };

  //==========================================================================================================

  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});

  //#region web Socket

  const getHearingFiltersRef = useRef<() => Record<string, any>>(() => ({}));
  const webSocketRequestQueueRef = useRef<string[]>([]);
  const ignoreWebSocketUntilRef = useRef(0);

  getHearingFiltersRef.current = () => {
    const placeId = isCountyFilter
      ? userCustomerProfile?.codeDistrict
      : userCustomerProfile?.placeId;
    const isFilterActive = savedFilter.isFilterActive;
    const userId = viewOnlyYourRecords ? user?.id : undefined;
    if (!isFilterActive) {
      return {
        placeId,
        userId,
        pageNumber: currentAudiencePage + 1,
        pageSize: audiencePageSize,
      };
    }

    const params: Record<string, any> = {
      placeId,
      district: savedFilter.isCountyFilter ?? false,
      procedureNumber: savedFilter.hearingNumber || undefined,
      startDate: savedFilter.startDate || undefined,
      finishDate: savedFilter.finishDate || undefined,
      status: savedFilter.statusFilter || undefined,
      hearingTypeId: savedFilter.hearingTypeIdFilter || undefined,
      attendantId: savedFilter.attendantFilterId || undefined,
      userId: userId,
      pageNumber: currentAudiencePage + 1,
      pageSize: audiencePageSize,
    };

    if (savedFilter.activeFilter !== null) {
      params.active = savedFilter.activeFilter;
    }

    return params;
  };

  useEffect(() => {
    const timer = setTimeout(() => setShouldConnect(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const WS_URL_HEARINGS = useMemo(
    () =>
      `${import.meta.env.VITE_APP_WSS_BASE_URL}/hearing?token=${accessToken}`,
    [accessToken],
  );
  const { sendMessage: sendMessageHearing } = useWebsocket(
    WS_URL_HEARINGS,
    {
      onMessage: (event: MessageEvent<any>) => {
        const responseFilter = webSocketRequestQueueRef.current.shift();
        if (Date.now() < ignoreWebSocketUntilRef.current) return;
        const currentFilters = getHearingFiltersRef.current();
        const currentFilterPayload = JSON.stringify(
          currentFilters,
          (_key, value) => (value === undefined ? undefined : value),
        );
        if (!responseFilter || responseFilter !== currentFilterPayload) return;

        handleChangeHearingsFromWebSocket(
          event.data,
          setAudiences,
          setTotalAudiences,
          currentFilters.placeId,
          currentFilters.district === true,
          setAudience,
        );
      },
      onOpen: () => console.log("✅ WebSocket conectado (ou reconectado)"),
      onClose: () => console.warn("⚠️ WebSocket desconectado"),
      shouldReconnect: () => true,
      reconnectInterval: 1000,
    },
    shouldConnect,
  );

  useEffect(() => {
    if (openHearingFilter) return;

    const interval = setInterval(() => {
      const filter = getHearingFiltersRef.current();
      const payload = JSON.stringify(filter, (_k, v) =>
        v === undefined ? undefined : v,
      );
      webSocketRequestQueueRef.current.push(payload);
      if (webSocketRequestQueueRef.current.length > 5) {
        webSocketRequestQueueRef.current.shift();
      }
      sendMessageHearing(payload);
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [openHearingFilter]);

  useEffect(() => {
    if (openModal) {
      return;
    }

    setConfidentialityLevel(selectedAudienceConfidentialityLevel);
    setIsClassifiedState(selectedAudienceIsClassified);
  }, [
    openModal,
    selectedAudienceConfidentialityLevel,
    selectedAudienceIsClassified,
  ]);

  //#endregion

  // const handleUpdateAudiences = (updatedAudiences: IAudience[]) => {
  //   setAudiences(updatedAudiences);
  // };

  const handleAddNewAttachment = () => setOpenAttachmentDrawer(true);

  const showAddButtonForlistRecorded = () => {
    return (
      !isConfidentialHearing ||
      (isConfidentialHearing && managerHearingClassified)
    );
  };

  return (
    <>
      <Container>
        <LoadSpinner loading={loading} zindex={1400} />
        <LeftList
          principalLabel={t("audiences.title")}
          audienceSelected={audience}
          onClick={setAudience}
          addClick={openModalCleared}
          placeId={userCustomerProfile?.placeId}
          audiences={audiences}
          totalCount={totalAudiences}
          currentPage={currentAudiencePage}
          pageCount={Math.ceil(totalAudiences / audiencePageSize)}
          onPageChange={handleAudiencePageChange}
          disabledFilter={false}
          isFilter={isFilter}
          filterClick={() => setOpenHearingFilter(true)}
          isLoading={loading}
          userId={user?.id}
          isInqueritoProduct={isInqueritoProduct}
        />

        {audience?.id ? (
          <ListRecorded
            setData={setListRecord}
            recordList={listRecord}
            audienceId={audience?.id}
            selectedRecordId={record?.id}
            setSelectRecord={changeRecord}
            placeId={userCustomerProfile?.placeId}
            addToast={addToast}
            showAddButton={showAddButtonForlistRecorded()}
            disabled={
              (isConfidentialHearing && disableIfNotCreate) || openModal
            }
            setLoading={setLoading}
          />
        ) : (
          <ThemeProvider theme={theme(customization)}>
            <AdminDashboard isFull={false} />
          </ThemeProvider>
        )}

        <Drawer
          title={isEdit ? t("audiences.edit") : t("audiences.create")}
          open={openModal}
          onClose={() => clear()}
          onApply={isEdit ? editAudience : handleSubmit}
          paddingLeft={0.1}
          paddingRight={0.1}
          paddingTop={0.1}
          paddingBottom={0.1}
        >
          {isInqueritoProduct ? (
            <CreateProcedure
              errorFields={errorFields}
              date={date}
              audience={audience}
              onChangeName={setNameAudience}
              procedureNumber={nameAudience}
              onChangeDate={setDate}
              isClassified={isClassified}
              setIsClassified={setIsClassified}
              editTypeSelected={editTypeSelected}
              setProcedureTypeId={setAudienceType}
              setObjectType={setObjectType}
              createAudienceMask={createAudienceMask}
              isEdit={isEdit}
              recordModeSelected={recordMode}
              setRecordMode={setRecordMode}
              onChangeDescription={setAudienceDescription}
              audienceDescription={audienceDescription}
              audienceStatus={audience?.status}
              customFields={customFields}
              showCustomFieldErrors={showCustomFieldErrors}
              customFieldValues={customFieldValues}
              onCustomFieldChange={handleCustomFieldChange}
              procedureCustomerSources={procedureCustomerSources}
              procedureCustomerSourceSelected={procedureCustomerSourceSelected}
              setProcedureCustomerSource={handleProcedureCustomerSourceChange}
            />
          ) : (
            <CreateAudience
              errorFields={errorFields}
              active={active}
              date={date}
              time={hours}
              audience={audience}
              onChangeSwitch={() => setActive(!active)}
              onChangeName={setNameAudience}
              procedureNumber={nameAudience}
              onChangeDate={setDate}
              onChangeHours={setHours}
              isClassified={isClassified}
              setIsClassified={setIsClassified}
              confidentialityLevel={confidentialityLevel}
              setConfidentialityLevel={setAudienceConfidentialityLevel}
              disabledConfidentialityLevel={!canChangeConfidentialityLevel}
              editTypeSelected={editTypeSelected}
              setProcedureTypeId={setAudienceType}
              setObjectType={setObjectType}
              createAudienceMask={createAudienceMask}
              isEdit={isEdit}
              recordModeSelected={recordMode}
              setRecordMode={setRecordMode}
              onChangeDescription={setAudienceDescription}
              audienceDescription={audienceDescription}
              audienceStatus={audience?.status}
              customFields={customFields}
              showCustomFieldErrors={showCustomFieldErrors}
              customFieldValues={customFieldValues}
              onCustomFieldChange={handleCustomFieldChange}
            />
          )}
        </Drawer>

        {audience && (
          <RightTemplate
            hearingId={audience?.id}
            onClickValidation={() => setOpenModalValidation(true)}
            hearingNumber={String(audience?.procedureNumber)}
            origin={audience.hearingTypeName ?? "-"}
            startDate={audience.expectedDate}
            endDate={
              audience?.finishDate ? new Date(audience?.finishDate) : new Date()
            }
            onClickRecoding={createHearingRecord}
            data={listRecord}
            navigateTo={
              recordProviderId === 2
                ? `/recording/mconf?hearingId=${record?.hearingId}&hearingFileId=${record?.id}`
                : `/recording/una?hearingId=${record?.hearingId}&hearingFileId=${record?.id}`
            }
            navigate={navigate}
            onClick={openModalEdit}
            addButton={() => setOpenDrawer(true)}
            onClickDetach={onSelectAttendantRemove as any}
            addNewAttachment={handleAddNewAttachment}
            onClickNotification={() => setOpenModalNotify(true)}
            onClickDownloads={() =>
              handleOpenDrawerDownloads(
                audience ? audience?.id : 0,
                setOpenDownloads,
                setListDownloads,
                setLoading,
                exportHearinDownloadsFiles,
              )
            }
            onClickDelete={handleClickDelete}
            onClickEdit={handleClickEdit}
            disabled={
              (isConfidentialHearing && disableIfNotCreate) || openModal
            }
            listAttendant={listAttendantInHearingFile}
            setListAttendant={setListAttendantInHearingFile}
            listRecord={listRecord?.hearingInProgress}
            record={getFirstRecord(listRecord)}
            audience={audience}
            customFields={customFields}
            applyReadOnlyOnSearchTerm={applyReadOnlyOnSearchTerm}
            onClickMigrateHearing={() => {
              setOpenModalVinculeDataToAnotherHearing(true);
            }}
            onClickVincule={() => setOpenServerLinkDrawer(true)}
          />
        )}

        <Drawer
          title={t("participants.link")}
          open={openDrawer}
          onClose={() => clearModal()}
          onApply={onVinculeAttendat}
          paddingLeft={33}
          paddingRight={33}
        >
          <ProcedureAttendant
            setAttendantId={setAttendantId}
            selected={attendantId}
            fetchParticipants={fetchParticipants}
            setAttendantList={setAttendantList}
            attendantList={attendantList}
            modalOpenNewParticipant={() => {
              setFieldError(false);
              setOpenModalCreate(true);
            }}
            fieldError={fieldError}
            attendantRoleId={attendantRoleId}
            hearingTypeId={audience?.hearingTypeId}
            attendantRoleDropSelectedIndex={attendantRoleIndex}
            setAttendantRoleDropSelected={handleSetAttendantRole}
          />
        </Drawer>

        <Drawer
          title={t("participants.create")}
          open={openModalCreate}
          onClose={() => clear()}
          paddingLeft={33}
          paddingRight={33}
          onApply={createAttendant}
        >
          <CreateEditAttendantInAudience
            onChangeName={setNameAttendant}
            onChangeDocument={setDocumentAttendant}
            onChangeEmail={setEmailAttendant}
            nameAttendant={nameAttendant}
            documentAttendant={documentAttendant}
            emailAttendant={emailAttendant}
            active={active}
            onChangeSwitch={() => setActive(!active)}
            fieldError={fieldErrorCreate}
          />
        </Drawer>
        <Drawer
          title={t("validation.videoAuthenticity")}
          open={openModalValidation}
          onClose={() => handleCloseModal()}
          onApply={loadingUpload ? () => {} : handleApplyValidationVideo}
          paddingLeft={0}
          paddingRight={0}
          paddingTop={0}
          disabled={loadingUpload}
        >
          <ValidationModal
            value={String(audience?.procedureNumber)}
            file={editItem ? { ...file, name: editItem?.filename } : file}
            setFile={setFile}
            editItem={editItem}
            fileError={fileError}
            formatFileSize={formatFileSize}
            setValidateVideo={setValidateVideo}
            setUploadingFile={setUploadingFile}
            uploadingFile={uploadingFile}
          />
        </Drawer>

        <Drawer
          title={t("audiences.serverLink.title")}
          open={openServerLinkDrawer}
          onClose={handleCloseServerLinkDrawer}
          onApply={handleApplyServerLink}
          paddingLeft={33}
          paddingRight={33}
        >
          <ServerLinkDrawerContent
            inputValue={serverLinkInput}
            loading={loadingServerLinkOptions}
            options={serverLinkOptions}
            selectedServers={[...linkedServers, ...serverLinksToCreate]}
            fieldError={serverLinkFieldError}
            onInputChange={(value) => {
              setServerLinkInput(value);
              setServerLinkFieldError(false);
            }}
            onSelect={handleSelectServerLink}
            onRemove={handleRemoveServerLink}
          />
        </Drawer>

        <Drawer
          title={t("audiences.migration.title")}
          open={openModalVinculeDataToAnotherHearing}
          onClose={() => handleCloseModal()}
          onApply={
            hearingCorrection !== undefined &&
            hearingCorrection &&
            hearingToMigrate
              ? () => setShowModalConfirmationVinculeDataToAnotherHearing(true)
              : () => {}
          }
          paddingLeft={0}
          paddingRight={0}
          paddingTop={0}
          disabled={!hearingToMigrate}
        >
          <VinculeDataToAnotherHearingModal
            value={String(audience?.procedureNumber)}
            placeId={userCustomerProfile?.placeId}
            setHearingToMigrate={setHearingToMigrate}
            setSwapModeToMigrate={setSwapModeToMigrate}
            currentHearingId={audience?.id}
            userId={user?.id}
          />
        </Drawer>
        <ModalDefault
          openModal={showModalConfirmationVinculeDataToAnotherHearing}
          setOpenModal={() =>
            setShowModalConfirmationVinculeDataToAnotherHearing(false)
          }
          title={t("audiences.migration.title")}
          bodyMessageTitle={t("audiences.migration.warning")}
          bodyMessageSubTitle={t("audiences.migration.confirmMessage")}
          textConfirm={t("audiences.migration.migrateButton")}
          onClickConfirm={() =>
            onMigrateDataToAnotherHearing(
              audience?.id,
              hearingToMigrate,
              swapModeToMigrate,
              setLoading,
              addToast,
              updateAudiences,
              () => {
                setShowModalConfirmationVinculeDataToAnotherHearing(false);
                handleCloseModal();
              },
            )
          }
          textCancel={t("common.cancel").toUpperCase()}
          onClickCancel={() =>
            setShowModalConfirmationVinculeDataToAnotherHearing(false)
          }
        />
        <Drawer
          title={t("notifications.title")}
          open={openModalNotify}
          onClose={() => {
            clear();
            handleCloseModal();
          }}
          onApply={() =>
            notify !== undefined && notify === true ? handleClickNotify() : {}
          }
          footer={notify !== undefined ? "apply" : "none"}
        >
          <ServiceIntegrationNotification
            inputValues={inputValues}
            setInputValues={setInputValues}
            notify={notify !== undefined ? true : false}
            hearingId={audience?.id}
          />
        </Drawer>

        <ModalDefault
          openModal={showModalConfirmation}
          setOpenModal={setShowModalConfirmation}
          title={t("participants.unlink")}
          bodyMessageTitle={
            <>
              {t("participants.messages.confirmUnlink", {
                name:
                  selectedCustomerAttendantName || t("participants.singular"),
              })}
            </>
          }
          bodyMessageSubTitle=""
          textConfirm={t("common.confirm").toUpperCase()}
          onClickConfirm={onClickDesvincule}
          textCancel={t("common.cancel").toUpperCase()}
          bodyMessageMarginBetweenTitleAndSubTitle="12px"
          bodyMessageMarginTop="20px"
          destructive
          cancelFirst
          onClickCancel={() => {
            setShowModalConfirmation(false);
          }}
        />

        {record && (
          <RightAudienceComponent
            record={record}
            onRecordChange={setRecord}
            disableFullView={record.status !== 13}
            title={t("recordings.details")}
            onClose={() => setRecord(null)}
            container={userCustomerProfile?.containerName}
            baseBlob={baseBlob}
            hearingService={HearingService}
            hearing={audience}
          />
        )}

        <DownloadsByHearingFile
          emptyMessage={t("downloads.emptyMessage")}
          title={t("downloads.title")}
          tooltipMessage={t("downloads.tooltip")}
          openDrawer={openDownloads}
          listDownloads={listDownloads}
          isInqueritoProduct={isInqueritoProduct}
          setOpenDrawer={setOpenDownloads}
          onApply={() => {}}
          onClose={() => setOpenDownloads(!openDownloads)}
          setItemClicked={(e: any) =>
            handleItemClicked(e, setLoading, addToast)
          }
          userId={user?.id}
        />

        <ShowModalDeleteAlert
          hearingId={hearingFileIdToDelete}
          setHearingFileIdToDelete={setHearingFileIdToDelete}
          OpenModalConfirmDelete={openModalConfirmDelete}
          setOpenModalConfirmDelete={setOpenModalConfirmDelete}
          listRecord={listRecord}
          setListRecord={setListRecord}
        />

        <CreateRecordEdit
          api={api}
          openEditRecord={openEditRecord}
          setOpenEditRecord={setOpenEditRecord}
          hearingFileToEdit={hearingFileToEdit}
          date={dateHearingFile}
          setDate={setDateHearingFile}
          time={timeHearingFile}
          setTime={setTimeHearingFile}
          setRecordEditedReturn={setRecordEditedReturn}
          isInqueritoProduct={isInqueritoProduct}
          customerId={user?.customerId}
        />

        <HearingFilter
          title={t("audiences.filter.title")}
          showDrawer={openHearingFilter}
          createAudienceMask={createAudienceMask}
          setShowDrawer={setOpenHearingFilter}
          hearingNumber={hearingNumber}
          setHearingNumber={setHearingNumber}
          startDate={startDate}
          setStartDate={setStartDate}
          finishDate={finishDate}
          setFinishDate={setFinishDate}
          active={activeFilter}
          setActive={setActiveFilter as any}
          valueOptionDropDown={valueOptionDropDown}
          setValueOptionDropDown={setValueOptionDropDown}
          onClickSearch={onClickSearch}
          options={options}
          clearDates={clearDatesFilter}
          onClickClear={onClickClear}
          startDateError={startDateError}
          finishDateError={finishDateError}
          onClose={onCloseModal}
          isCounty={isCountyFilter}
          setIsCounty={setIsCountyFilter}
          hearingTypeId={hearingTypeIdFilter}
          setHearingTypeId={setHearingTypeIdFilter}
          hearingSourceId={hearingSourceIdFilter}
          setHearingSourceId={setHearingSourceIdFilter}
          status={statusFilter}
          setStatus={setStatusFilter}
          userCustomerProfile={userCustomerProfile}
          setAttendantFilterId={setAttendantFilterId}
          attendantFilterId={attendantFilterId}
        />
        {isInqueritoProduct ? (
          <CreateAttachmentTemplateInquerito
            openModal={openAttachmentDrawer}
            setOpenModal={setOpenAttachmentDrawer}
            hearingId={audience?.id}
            listRecord={listRecord}
          />
        ) : (
          <CreateAttachmentTemplateAudiencia
            openModal={openAttachmentDrawer}
            setOpenModal={setOpenAttachmentDrawer}
            hearingId={audience?.id}
            listRecord={listRecord}
          />
        )}
      </Container>
    </>
  );
};
